import { useId, useState } from "react"
import { useMoveMetaAccountAssignmentsMutation } from "~/ui-rtk/api/metaAccountApi"
import type {
  MetaAccountUser,
  MoveMetaAccountAssignmentsResult,
} from "~/ui-rtk/api/types/meta-account.types"
// Direct imports (not the `common` / `controls` barrels) — see CreativesTab.tsx.
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import Select from "~/ui-rtk/components/ui/controls/Select"
import { getServerErrorMessage } from "~/ui-rtk/utils/http-utils"
import { DIALOG_CONTAINER_CLS } from "./AssignMetaAccessDialog"
import {
  BUTTON,
  MOVE_FAILED,
  MOVE_TO_LABEL,
  NOT_MOVED_LABEL,
  loginLabelOf,
  moveButtonText,
  moveTitle,
  movedText,
  notMovedLine,
  unusableSuffixOf,
} from "./copy"
import { isUsableLogin } from "./meta-access.utils"

export interface MoveAssignmentsDialogProps {
  from: MetaAccountUser
  users: MetaAccountUser[]
  onClose: () => void
}

/**
 * Move every assignment of one login to another (APP-566 T2), e.g. to a
 * backup person. The API checks each company's assets against the target
 * login and moves only the ones it reaches; the rest stay, with the reason.
 * The dialog itself is the confirmation: nothing moves until "Move".
 */
export default function MoveAssignmentsDialog({
  from,
  users,
  onClose,
}: MoveAssignmentsDialogProps) {
  const fieldId = useId()
  const targets = users.filter(user => user.id !== from.id)
  const [toId, setToId] = useState(
    () => targets.find(user => isUsableLogin(user.health))?.id ?? "",
  )
  const [move, { isLoading: moving }] = useMoveMetaAccountAssignmentsMutation()
  const [result, setResult] = useState<MoveMetaAccountAssignmentsResult | null>(
    null,
  )
  const [error, setError] = useState<string | null>(null)
  const target = targets.find(user => user.id === toId)
  const canMove = !moving && !result && !!target && isUsableLogin(target.health)

  const submit = async () => {
    if (!canMove) return
    setError(null)
    try {
      setResult(await move({ id: from.id, toCredentialId: toId }).unwrap())
    } catch (err) {
      setError(getServerErrorMessage(err, MOVE_FAILED))
    }
  }

  return (
    <Dialog
      isOpen
      handleClose={onClose}
      width={520}
      classes={{ container: DIALOG_CONTAINER_CLS }}
    >
      <h2 className="text-lg font-semibold text-white">
        {moveTitle(from.connectedUser?.name ?? null)}
      </h2>

      <div className="flex flex-col gap-1">
        <label htmlFor={`${fieldId}-to`}>{MOVE_TO_LABEL}</label>
        <Select
          id={`${fieldId}-to`}
          options={targets.map(user => ({
            value: user.id,
            label: `${loginLabelOf(user.connectedUser)}${unusableSuffixOf(user.health)}`,
            disabled: !isUsableLogin(user.health),
          }))}
          value={toId}
          onChange={({ target: select }) => setToId(select.value)}
          disabled={moving || result !== null}
        />
      </div>

      {result && (
        <div data-testid="move-result" className="flex flex-col gap-1">
          <p className="text-white">
            {movedText(
              result.moved.length,
              result.moved.length + result.notMoved.length,
            )}
          </p>
          {result.notMoved.length > 0 && (
            <>
              <p>{NOT_MOVED_LABEL}</p>
              <ul className="list-disc pl-5">
                {result.notMoved.map(({ companyId, companyName, reason }) => (
                  <li key={companyId}>{notMovedLine(companyName, reason)}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {error && (
        <p role="alert" className="text-states-error">
          {error}
        </p>
      )}

      <div className="mt-1 flex flex-wrap items-center justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={moving}
          className="btn btn-ghost btn-sm"
        >
          {result ? BUTTON.close : BUTTON.cancel}
        </button>
        {!result && (
          <button
            type="button"
            onClick={() => void submit()}
            disabled={!canMove}
            className="btn btn-primary btn-sm"
          >
            {moving && <span className="loading loading-spinner loading-xs" />}
            {moveButtonText(from.assignmentCount)}
          </button>
        )}
      </div>
    </Dialog>
  )
}
